import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumGermanBeerDutyMovementType } from "./enums/enumGermanBeerDutyMovementType.generated";
import { EnumGermanBeerDutyProductType } from "./enums/enumGermanBeerDutyProductType.generated";
import { EnumSpiritDutyExemptionReason } from "./enums/enumSpiritDutyExemptionReason.generated";
import { EnumStockTransferStatus } from "./enums/enumStockTransferStatus.generated";

// Model Objects

export class TransferHeaderReadOnly extends ModelObject<TransferHeaderReadOnly> {

    /**
    * Transfer Number
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public number: string = "";
    public status: EnumStockTransferStatus = EnumStockTransferStatus.none;
    /**
    * User Id of the creating user.
    */
    public createdByUserId: Guid = Guid.createEmpty();
    /**
    * Timestamp at which the stock transfer was created. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    private _cancelledByUserId: Guid | null = null;
    /**
    * User who cancelled the order. Captured by system not user editable. (Null if not cancelled).
    */
    public get cancelledByUserId(): Guid | null { return this._cancelledByUserId }
    public set cancelledByUserId(value: Guid | null) {
      this._cancelledByUserId = value === undefined ? null : value;
    }
    private _cancelledAt: Instant | null = null;
    /**
    * Timestamp at which the stock transfer was last cancelled. Captured by system not user editable. (Null if not completed).
    */
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    private _completedByUserId: Guid | null = null;
    /**
    * User who completed the order. Captured by system not user editable. (Null if not completed).
    */
    public get completedByUserId(): Guid | null { return this._completedByUserId }
    public set completedByUserId(value: Guid | null) {
      this._completedByUserId = value === undefined ? null : value;
    }
    private _completedAt: Instant | null = null;
    /**
    * Timestamp at which the stock transfer was last completed. Captured by system not user editable. (Null if not completed).
    */
    public get completedAt(): Instant | null { return this._completedAt }
    public set completedAt(value: Instant | null) {
      this._completedAt = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.number = jsonSerializedObject.number;
          this.status = EnumStockTransferStatus.getByValue(jsonSerializedObject.status, this.status);
          this.createdByUserId = Guid.parseOrEmpty(jsonSerializedObject.createdByUserId);
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.cancelledByUserId = jsonSerializedObject.cancelledByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.cancelledByUserId);
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.completedByUserId = jsonSerializedObject.completedByUserId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.completedByUserId);
          this.completedAt = jsonSerializedObject.completedAt == null ? null : Instant.parse(jsonSerializedObject.completedAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.number = this.number;
        toRet.status = this.status.value;
        toRet.createdByUserId = this.createdByUserId.toString();
        toRet.createdAt = this.createdAt.toString();
        toRet.cancelledByUserId = this.cancelledByUserId == null ? null : this.cancelledByUserId.toString();
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.completedByUserId = this.completedByUserId == null ? null : this.completedByUserId.toString();
        toRet.completedAt = this.completedAt == null ? null : this.completedAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdByUserId", rule: (v: any) => !v.isEmpty() || "TransferHeaderReadOnly.CreatedByUserId cannot be empty"},
            {field: "cancelledByUserId", rule: (v: any) => v == null || !v.isEmpty() || "TransferHeaderReadOnly.CancelledByUserId cannot be empty"},
            {field: "completedByUserId", rule: (v: any) => v == null || !v.isEmpty() || "TransferHeaderReadOnly.CompletedByUserId cannot be empty"},
    ];
}


export class StockTransferHeader extends ModelObject<StockTransferHeader> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Delivery Date
    */
    public deliveryDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * Despatch Date
    */
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * From Stock Location
    */
    public fromStockLocationId: Guid = Guid.createEmpty();
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * To Stock Location
    */
    public toStockLocationId: Guid = Guid.createEmpty();
    public readOnly: TransferHeaderReadOnly = new TransferHeaderReadOnly();
    /**
    * German Duty Implication for Beer leaving the FROM location. Only limited valid options for Bonded/NonBonded combinations of From/To. Bonded->Bonded = NonTaxableToGermany or NonTaxableToEU. Bonded->NonBonded = Taxable.NonBonded->Bonded = None.NonBonded->NonBonded = None.
    */
    public germanBeerDutyMovementTypeAtFrom: EnumGermanBeerDutyMovementType = EnumGermanBeerDutyMovementType.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.deliveryDate = LocalDate.parse(jsonSerializedObject.deliveryDate);
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.fromStockLocationId = Guid.parseOrEmpty(jsonSerializedObject.fromStockLocationId);
          this.notes = jsonSerializedObject.notes;
          this.toStockLocationId = Guid.parseOrEmpty(jsonSerializedObject.toStockLocationId);
          this.readOnly = new TransferHeaderReadOnly(jsonSerializedObject.readOnly);
          this.germanBeerDutyMovementTypeAtFrom = EnumGermanBeerDutyMovementType.getByValue(jsonSerializedObject.germanBeerDutyMovementTypeAtFrom, this.germanBeerDutyMovementTypeAtFrom);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.deliveryDate = this.deliveryDate.toString();
        toRet.despatchDate = this.despatchDate.toString();
        toRet.fromStockLocationId = this.fromStockLocationId.toString();
        toRet.notes = this.notes;
        toRet.toStockLocationId = this.toStockLocationId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.germanBeerDutyMovementTypeAtFrom = this.germanBeerDutyMovementTypeAtFrom.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockTransferHeader.Id cannot be empty"},
            {field: "fromStockLocationId", rule: (v: any) => !v.isEmpty() || "StockTransferHeader.FromStockLocationId cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "toStockLocationId", rule: (v: any) => !v.isEmpty() || "StockTransferHeader.ToStockLocationId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class StockTransferLineDetailReadOnly extends ModelObject<StockTransferLineDetailReadOnly> {

    /**
    * Stock Item ABV
    * Not Negative
    */
    public stockItemAbv: number = 0;
    /**
    * Alcohol Type
    */
    public alcoholType: EnumAlcoholType = EnumAlcoholType.none;
    /**
    * Stock Dutiable Litres
    * Not Negative
    */
    public unitDutiableLitres: number = 0;
    private _manufacturerId: Guid | null = null;
    /**
    * Manufacturer of the product - Required for lines with Alcohol.
    */
    public get manufacturerId(): Guid | null { return this._manufacturerId }
    public set manufacturerId(value: Guid | null) {
      this._manufacturerId = value === undefined ? null : value;
    }
    /**
    * Stock Transfer Id
    */
    public stockTransferId: Guid = Guid.createEmpty();
    public sequence: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemAbv = jsonSerializedObject.stockItemAbv;
          this.alcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.alcoholType, this.alcoholType);
          this.unitDutiableLitres = jsonSerializedObject.unitDutiableLitres;
          this.manufacturerId = jsonSerializedObject.manufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.manufacturerId);
          this.stockTransferId = Guid.parseOrEmpty(jsonSerializedObject.stockTransferId);
          this.sequence = jsonSerializedObject.sequence;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemAbv = this.stockItemAbv;
        toRet.alcoholType = this.alcoholType.value;
        toRet.unitDutiableLitres = this.unitDutiableLitres;
        toRet.manufacturerId = this.manufacturerId == null ? null : this.manufacturerId.toString();
        toRet.stockTransferId = this.stockTransferId.toString();
        toRet.sequence = this.sequence;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "manufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "StockTransferLineDetailReadOnly.ManufacturerId cannot be empty"},
            {field: "stockTransferId", rule: (v: any) => !v.isEmpty() || "StockTransferLineDetailReadOnly.StockTransferId cannot be empty"},
            {field: "sequence", rule: (v: any) => v == null || v >= -2147483648 || "Sequence is not greater than minimum allowed value: -2147483648"},
            {field: "sequence", rule: (v: any) => v == null || v <= 2147483647 || "Sequence is above maximum allowed value: 2147483647"},
    ];
}


export class StockTransferLineDetail extends ModelObject<StockTransferLineDetail> {

    private _dateOfBatch: LocalDate | null = null;
    public get dateOfBatch(): LocalDate | null { return this._dateOfBatch }
    public set dateOfBatch(value: LocalDate | null) {
      this._dateOfBatch = value === undefined ? null : value;
    }
    public stockItemId: Guid = Guid.createEmpty();
    public urn: string = "";
    /**
    * Quantity
    */
    public quantity: number = 0;
    /**
    * Unit of Measure
    */
    public unitOfMeasureId: Guid = Guid.createEmpty();
    public readOnly: StockTransferLineDetailReadOnly = new StockTransferLineDetailReadOnly();
    private _plato: number | null = null;
    /**
    * Plato Value
    */
    public get plato(): number | null { return this._plato }
    public set plato(value: number | null) {
      this._plato = value === undefined ? null : value;
    }
    public germanBeerDutyProductType: EnumGermanBeerDutyProductType = EnumGermanBeerDutyProductType.none;
    private _germanBeerMixedProductPercentageA: number | null = null;
    public get germanBeerMixedProductPercentageA(): number | null { return this._germanBeerMixedProductPercentageA }
    public set germanBeerMixedProductPercentageA(value: number | null) {
      this._germanBeerMixedProductPercentageA = value === undefined ? null : value;
    }
    /**
    * Set true if this item is brewed by members of the public for their personal use by borrowing the Brewery's equipment. This is a service offered by breweries in some countries, such as Australia, and can affect duty.
    */
    public isBrewedForPersonalUse: boolean = false;
    /**
    * Set true if this item is designed to be connected to a pump or pressurised gas system, such as those used in pubs. Some duty systems have different duty rates for beer packaged in these manners for selling to pubs.
    */
    public isDesignedForPump: boolean = false;
    private _finalUnitLitres: number | null = null;
    /**
    * Litres held by the final consumable packaged unit, if relevant. For example, a case of 12 x 500ml bottles would have a final unit litres of 0.5. Some duty systems have different rates depending upon this value.
    * Greater Than : 0
    */
    public get finalUnitLitres(): number | null { return this._finalUnitLitres }
    public set finalUnitLitres(value: number | null) {
      this._finalUnitLitres = value === undefined ? null : value;
    }
    public spiritDutyExemptionReason: EnumSpiritDutyExemptionReason = EnumSpiritDutyExemptionReason.none;
    private _dateOfProductionDutyRate: LocalDate | null = null;
    /**
    * An optional date identifying the duty rate in force at the time the item was produced. Used for UK alcohol duty, where products qualifying for SPR are charged at the rate in force at date of production, not date of crossing duty boundary. To make management easier we only track the date to granularity of rate changes, So eg a value of 2023-08-01 would represent the period from 2023-08-01 to 2024-01-31 because rates did not change in this period.
    */
    public get dateOfProductionDutyRate(): LocalDate | null { return this._dateOfProductionDutyRate }
    public set dateOfProductionDutyRate(value: LocalDate | null) {
      this._dateOfProductionDutyRate = value === undefined ? null : value;
    }
    public id: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.dateOfBatch = jsonSerializedObject.dateOfBatch == null ? null : LocalDate.parse(jsonSerializedObject.dateOfBatch);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.quantity = jsonSerializedObject.quantity;
          this.unitOfMeasureId = Guid.parseOrEmpty(jsonSerializedObject.unitOfMeasureId);
          this.readOnly = new StockTransferLineDetailReadOnly(jsonSerializedObject.readOnly);
          this.plato = jsonSerializedObject.plato == null ? null : jsonSerializedObject.plato;
          this.germanBeerDutyProductType = EnumGermanBeerDutyProductType.getByValue(jsonSerializedObject.germanBeerDutyProductType, this.germanBeerDutyProductType);
          this.germanBeerMixedProductPercentageA = jsonSerializedObject.germanBeerMixedProductPercentageA == null ? null : jsonSerializedObject.germanBeerMixedProductPercentageA;
          this.isBrewedForPersonalUse = jsonSerializedObject.isBrewedForPersonalUse;
          this.isDesignedForPump = jsonSerializedObject.isDesignedForPump;
          this.finalUnitLitres = jsonSerializedObject.finalUnitLitres == null ? null : jsonSerializedObject.finalUnitLitres;
          this.spiritDutyExemptionReason = EnumSpiritDutyExemptionReason.getByValue(jsonSerializedObject.spiritDutyExemptionReason, this.spiritDutyExemptionReason);
          this.dateOfProductionDutyRate = jsonSerializedObject.dateOfProductionDutyRate == null ? null : LocalDate.parse(jsonSerializedObject.dateOfProductionDutyRate);
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.dateOfBatch = this.dateOfBatch == null ? null : this.dateOfBatch.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.quantity = this.quantity;
        toRet.unitOfMeasureId = this.unitOfMeasureId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.plato = this.plato == null ? null : this.plato;
        toRet.germanBeerDutyProductType = this.germanBeerDutyProductType.value;
        toRet.germanBeerMixedProductPercentageA = this.germanBeerMixedProductPercentageA == null ? null : this.germanBeerMixedProductPercentageA;
        toRet.isBrewedForPersonalUse = this.isBrewedForPersonalUse;
        toRet.isDesignedForPump = this.isDesignedForPump;
        toRet.finalUnitLitres = this.finalUnitLitres == null ? null : this.finalUnitLitres;
        toRet.spiritDutyExemptionReason = this.spiritDutyExemptionReason.value;
        toRet.dateOfProductionDutyRate = this.dateOfProductionDutyRate == null ? null : this.dateOfProductionDutyRate.toString();
        toRet.id = this.id.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "StockTransferLineDetail.StockItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
            {field: "unitOfMeasureId", rule: (v: any) => !v.isEmpty() || "StockTransferLineDetail.UnitOfMeasureId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "finalUnitLitres", rule: (v: any) => v == null || v > 0 || "FinalUnitLitres is not greater than minimum allowed value: 0"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockTransferLineDetail.Id cannot be empty"},
    ];
}


export class StockTransfer extends ModelObjectWithDirtyCheck<StockTransfer> {

    public header: StockTransferHeader = new StockTransferHeader();
    public lineDetails: (StockTransferLineDetail)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.header = new StockTransferHeader(jsonSerializedObject.header);
          this.lineDetails = jsonSerializedObject.lineDetails.map((v: any) => new StockTransferLineDetail(v));
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.header = this.header.toJsonSerializedObject();
        toRet.lineDetails = this.lineDetails.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "header", rule: (v: any) => !!v || "header is required"},
            {field: "header", rule: (v: any) => v.validate()},
            {field: "lineDetails", rule: (v: any) => !!v || "lineDetails is required"},
            {field: "lineDetails", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTransferLineDetail[]).reduce((acc: Array<string>, curr: StockTransferLineDetail) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockTransferFilter extends ModelObject<StockTransferFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromDespatchDateInclusive: LocalDate | null = null;
    /**
    * Minimum (inclusive) despatch date
    */
    public get fromDespatchDateInclusive(): LocalDate | null { return this._fromDespatchDateInclusive }
    public set fromDespatchDateInclusive(value: LocalDate | null) {
      this._fromDespatchDateInclusive = value === undefined ? null : value;
    }
    private _toDespatchDateInclusive: LocalDate | null = null;
    /**
    * Maximum (inclusive) despatch date
    */
    public get toDespatchDateInclusive(): LocalDate | null { return this._toDespatchDateInclusive }
    public set toDespatchDateInclusive(value: LocalDate | null) {
      this._toDespatchDateInclusive = value === undefined ? null : value;
    }
    private _limitStatuses: readonly (EnumStockTransferStatus)[] | null = null;
    public get limitStatuses(): readonly (EnumStockTransferStatus)[] | null { return this._limitStatuses }
    public set limitStatuses(value: readonly (EnumStockTransferStatus)[] | null) {
      this._limitStatuses = value === undefined ? null : value;
    }
    private _stockTransferIds: readonly (Guid)[] | null = null;
    public get stockTransferIds(): readonly (Guid)[] | null { return this._stockTransferIds }
    public set stockTransferIds(value: readonly (Guid)[] | null) {
      this._stockTransferIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromDespatchDateInclusive = jsonSerializedObject.fromDespatchDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.fromDespatchDateInclusive);
          this.toDespatchDateInclusive = jsonSerializedObject.toDespatchDateInclusive == null ? null : LocalDate.parse(jsonSerializedObject.toDespatchDateInclusive);
          this.limitStatuses = jsonSerializedObject.limitStatuses == null ? null : jsonSerializedObject.limitStatuses.map((v: any) => EnumStockTransferStatus.getByValue(v));
          this.stockTransferIds = jsonSerializedObject.stockTransferIds == null ? null : jsonSerializedObject.stockTransferIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromDespatchDateInclusive = this.fromDespatchDateInclusive == null ? null : this.fromDespatchDateInclusive.toString();
        toRet.toDespatchDateInclusive = this.toDespatchDateInclusive == null ? null : this.toDespatchDateInclusive.toString();
        toRet.limitStatuses = this.limitStatuses == null ? null : this.limitStatuses.map(v => v.value);
        toRet.stockTransferIds = this.stockTransferIds == null ? null : this.stockTransferIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredStockTransferHeaderResults extends ModelObject<FilteredStockTransferHeaderResults> {

    public matchingCount: number = 0;
    public stockTransferHeaders: (StockTransferHeader)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.stockTransferHeaders = jsonSerializedObject.stockTransferHeaders.map((v: any) => new StockTransferHeader(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.stockTransferHeaders = this.stockTransferHeaders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "stockTransferHeaders", rule: (v: any) => !!v || "stockTransferHeaders is required"},
            {field: "stockTransferHeaders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as StockTransferHeader[]).reduce((acc: Array<string>, curr: StockTransferHeader) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class StockTransferService extends ServiceBase {

  public async getAllStockTransferHeaders(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockTransferHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.stockTransferHeaders.map((v: any) => new StockTransferHeader(v));;
    return this.makeJsonRequest("stockTransfer/v1/getAllStockTransferHeaders", req, false, extractor, opts);
  };

  public async getFilteredStockTransferHeaders(filter: StockTransferFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredStockTransferHeaderResults> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredStockTransferHeaderResults(response.results);;
    return this.makeJsonRequest("stockTransfer/v1/getFilteredStockTransferHeaders", req, false, extractor, opts);
  };

  public async getStockTransferHeader(stockTransferId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTransferHeader> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    };
    const extractor = (response:any) => new StockTransferHeader(response.stockTransferHeader);;
    return this.makeJsonRequest("stockTransfer/v1/getStockTransferHeader", req, false, extractor, opts);
  };

  public async getStockTransferHeaders(stockTransferIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockTransferHeader)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferIds:stockTransferIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockTransferHeaders.map((v: any) => new StockTransferHeader(v));;
    return this.makeJsonRequest("stockTransfer/v1/getStockTransferHeaders", req, false, extractor, opts);
  };

  public async getStockTransfer(stockTransferId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockTransfer> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    };
    const extractor = (response:any) => new StockTransfer(response.stockTransfer);;
    return this.makeJsonRequest("stockTransfer/v1/getStockTransfer", req, false, extractor, opts);
  };

  public async getFilteredStockTransfers(filter: StockTransferFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(StockTransfer)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => response.stockTransfers.map((v: any) => new StockTransfer(v));;
    return this.makeJsonRequest("stockTransfer/v1/getFilteredStockTransfers", req, false, extractor, opts);
  };

  public async saveStockTransfer(stockTransfer: StockTransfer, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransfer:stockTransfer.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTransfer/v1/saveStockTransfer", req, true, extractor, opts);
  };

  public async completeStockTransfer(stockTransferId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTransfer/v1/completeStockTransfer", req, true, extractor, opts);
  };

  public async cancelStockTransfer(stockTransferId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTransfer/v1/cancelStockTransfer", req, true, extractor, opts);
  };

  public async generateStockTransferNote(stockTransferId: Guid, documentFormat: EnumDocumentFileType, documentTemplateId: Guid | null, quantity: number, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    documentFormat:documentFormat.value,
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    quantity:quantity,
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.documentBytes);;
    return this.makeJsonRequest("stockTransfer/v1/generateStockTransferNote", req, false, extractor, opts);
  };

  public async cancelCompletedStockTransfer(stockTransferId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransferId:stockTransferId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockTransfer/v1/cancelCompletedStockTransfer", req, true, extractor, opts);
  };

  public async getApproximateDutyForStockTransfer(stockTransfer: StockTransfer, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<number> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockTransfer:stockTransfer.toJsonSerializedObject(),
    };
    const extractor = (response:any) => response.approximateDuty;;
    return this.makeJsonRequest("stockTransfer/v1/getApproximateDutyForStockTransfer", req, false, extractor, opts);
  };
}

export const stockTransferService = new StockTransferService();